import React, { type JSX } from 'react';

import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { transactionsUtils } from 'app-components/flux/transactions';
import { isOverflowSplitItem } from 'app-components/flux/transactions/splitsHelpers';
import { splitItemsToArrayOverflowLast } from 'app-components/flux/transactions/splitsHelpers';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';
import SplitsView from 'components/TransactionSplits/SplitsView';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

const useStyles = makeStyles()((theme) => ({
  splitLine: {
    color: theme.palette.text.secondary,
    paddingTop: theme.spacing(0.75),
    display: 'flex',
    alignItems: 'center',
  },
  splitButton: {
    marginRight: theme.spacing(1),
  },
  splitIcon: {
    marginRight: theme.spacing(1),
    letterSpacing: 1,
    color: theme.palette.icon.main,
  },
  fullWidthSplitButton: {
    width: '100%',
    minHeight: 38,
    padding: theme.spacing(0.75, 2),
    borderRadius: theme.shape.radius.xl,
    border: `1px solid ${theme.palette.border.main}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.primary.main,
    },
  },
  fullWidthSplitIcon: {
    height: 22,
    width: 22,
    color: theme.palette.icon.main,
  },
}));

/**
 * The split items this component actually renders as "assigned" — filters
 * out the overflow line by default (it represents the unallocated
 * remainder, not a real split) unless the transaction is bank-matched or the
 * caller explicitly wants it included. Exported so callers can check whether
 * splits are assigned without duplicating this filtering.
 */
export const getAssignedSplitItems = (
  transaction: TransactionProps,
  { includeOverflowSplitLine = false }: { includeOverflowSplitLine?: boolean } = {},
) => {
  const splitItems = splitItemsToArrayOverflowLast(transaction.split?.items || []);
  const isMatchedTxn = transactionsUtils.getMatchState(transaction) === 'matched';
  return splitItems.filter((item) => includeOverflowSplitLine || isMatchedTxn || !isOverflowSplitItem(item));
};

type Props = {
  transaction: TransactionProps;
  onAssignSplits?: () => void;
  buttonLabel?: string;
  buttonSupportingText?: string;
  goToOtherSide?: (txn: TransactionProps) => void;
  disabledSplitsEdit?: boolean;
  viewWhenDisabled?: boolean;
  includeOverflowSplitLine?: boolean;
  /**
   * Use `series` when assigning splits to a recurring series.
   * Default is `default` for normal transaction splits.
   */
  variant?: 'default' | 'series';
  /**
   * Drawer redesign: re-compose for the narrow single column. The empty-state entry point becomes a
   * full-width bordered button (centered icon + label, no inline supporting text) instead of the
   * inline "Split · supporting text" row, and the assigned-splits list (SplitsView) renders as a
   * single-column stack with icon-prefixed sections (tags / clients & projects / note). Off = the
   * modal button + grid, unchanged.
   */
  drawerLayout?: boolean;
};

const TransactionSplits = ({
  transaction,
  onAssignSplits,
  buttonLabel,
  buttonSupportingText,
  goToOtherSide,
  disabledSplitsEdit = false,
  viewWhenDisabled = false,
  includeOverflowSplitLine = false,
  variant = 'default',
  drawerLayout = false,
}: Props): JSX.Element => {
  const { classes } = useStyles();

  const splitsFeatureFlag = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const isSeriesVariant = variant === 'series';
  const splitsEditDisabled = disabledSplitsEdit ?? !splitsFeatureFlag;

  const txnAmount = transaction.amount;
  const scheduledModel = useAppSelector((state) =>
    transaction.stModelId
      ? scheduledTransactionsSelectors.getScheduledTransactionById(state, transaction.stModelId)
      : undefined,
  );

  if (isSeriesVariant && !splitsFeatureFlag) return <React.Fragment />;

  const seriesTxn = scheduledModel?.transaction ?? null;
  const filteredSplitItems = getAssignedSplitItems(transaction, { includeOverflowSplitLine });
  const headerLabel = `${filteredSplitItems.length} Splits assigned`;
  const hasSplitsAssigned = filteredSplitItems.length > 0;

  const resolvedButtonLabel = buttonLabel ?? (isSeriesVariant ? 'Assign Splits' : 'Split');
  const resolvedButtonSupportingText =
    buttonSupportingText ??
    (isSeriesVariant
      ? "When transactions are matched to this Series, they'll be split in the same way."
      : 'Assign multiple tags or categories to this transaction.');

  return (
    <>
      {hasSplitsAssigned ? (
        <SplitsView
          txnId={transaction.id}
          splitItems={filteredSplitItems}
          amount={txnAmount}
          headerLabel={headerLabel}
          showRepeatIcon={Boolean(seriesTxn && transactionsUtils.isSplitTxn(seriesTxn))}
          onEdit={splitsEditDisabled && !viewWhenDisabled ? undefined : onAssignSplits}
          disabledEdit={splitsEditDisabled}
          goToOtherSide={goToOtherSide}
          variant={variant}
          drawerLayout={drawerLayout}
        />
      ) : drawerLayout ? (
        <Button
          id='assign-splits-button'
          onClick={onAssignSplits}
          className={classes.fullWidthSplitButton}
          style={{ textTransform: 'none' }}
          disabled={splitsEditDisabled}
          startIcon={<CallSplitRoundedIcon className={classes.fullWidthSplitIcon} />}
        >
          {resolvedButtonLabel}
        </Button>
      ) : (
        <Typography variant='captionRegular' className={classes.splitLine}>
          <Button
            id='assign-splits-button'
            onClick={onAssignSplits}
            className={classes.splitButton}
            disabled={splitsEditDisabled}
            startIcon={<CallSplitRoundedIcon className={classes.splitIcon} />}
          >
            {resolvedButtonLabel}
          </Button>
          {resolvedButtonSupportingText}
        </Typography>
      )}
    </>
  );
};

export default TransactionSplits;
